/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { useGlobalAppStorage } from '../../providers/GlobalContextProvider';

import type { ColumnOrderProps } from '../interfaces/column-order-props';
import type { SortProps } from '../interfaces/sort-props';
import type { AppStorage } from '../../../helpers/storages';
import type { ColumnResizeProps, ColumnToggleProps, Filter, GridStorageActions, GridStorageProps, ColumnStorageProps } from '../interfaces';

export const useGridStorageActions = <FilterT>(componentName: string, stateId = ''): GridStorageActions<FilterT> | null => {
    const appStorage: AppStorage = useGlobalAppStorage() ?? {} as AppStorage;
    const gridKey = `${componentName}-${stateId}`;
    const getGridData = (): GridStorageProps<FilterT> | null => appStorage.getObjItem(gridKey);
    const setGridData = (data: GridStorageProps<FilterT> | null): void => {
        if (data) {
            appStorage.setObjItem(gridKey, data);
        } else {
            appStorage.removeItem(gridKey);
        }
    };
    const onColumnChanges = (newColumns: Array<ColumnToggleProps | ColumnResizeProps | ColumnOrderProps>) => {
        const gridData = getGridData();
        const columns = Object.fromEntries(newColumns.map(column => [column.columnId, { ...gridData?.columns?.[column.columnId], ...column }]));

        setGridData({ ...gridData, columns });
    };
    const onFilterChange = (filters: Filter<FilterT>) => {
        setGridData({ ...getGridData(), filters });
    };
    const onSortChange = (sort: SortProps) => {
        setGridData({ ...getGridData(), sort });
    };
    const getColumnsData = (): Record<string, ColumnStorageProps> | null => getGridData()?.columns ?? null;
    const getSortData = (): SortProps | null => getGridData()?.sort ?? null;
    const getFilterData = (): Filter<FilterT> | null => getGridData()?.filters ?? null;

    if (stateId && typeof appStorage.getObjItem !== 'function') {
        console.error('No AppStorage in React Context');
    }

    if (stateId) {
        return {
            onColumnChanges,
            onFilterChange,
            onSortChange,

            getColumnsData,
            getSortData,
            getFilterData,
        };
    }

    return null;
};
